import { ReactNode } from 'react';
import { useAppReadiness } from '@/contexts/AppReadinessContext';

import { motion } from 'framer-motion';

interface AppLoadingGateProps {
  children: ReactNode;
}

/**
 * AppLoadingGate prevents rendering children until critical data is loaded.
 * Shows a smooth loading state instead of broken/thrashed UI.
 */
export function AppLoadingGate({ children }: AppLoadingGateProps) {
  const { isAppReady, isCriticalDataLoading, hasCriticalError } = useAppReadiness();
  

  // If there's a critical error, still render children - ErrorDisplay will handle it
  if (hasCriticalError) {
    return <>{children}</>;
  }

  // Show loading state while critical data is loading
  if (!isAppReady && isCriticalDataLoading) {
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          {/* Animated logo container with spinning ring */}
          <div className="relative w-24 h-24 flex items-center justify-center">
            {/* Spinning gradient ring */}
            <div className="absolute inset-0 rounded-full border-[3px] border-transparent bg-gradient-to-r from-primary via-primary/50 to-transparent bg-origin-border animate-spin" 
                 style={{ 
                   backgroundClip: 'padding-box, border-box',
                   background: 'conic-gradient(from 0deg, hsl(var(--primary)), hsl(var(--primary) / 0.3), transparent)',
                   mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                   maskComposite: 'xor',
                   WebkitMaskComposite: 'xor',
                   padding: '3px',
                   animationDuration: '1.2s'
                 }} 
            />
            {/* Logo */}
            <img 
              src="/logo.png" 
              alt="BuxBox" 
              className="w-14 h-14 object-contain rounded-xl"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return <>{children}</>;
}
